<template>
  <div class="pb-10">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <div v-for="(blog, idx) in blogs" :key="idx + blog?.slug">
        <PagesBlogCard :blog="blog" />
      </div>
    </div>
    <div
      v-if="blogs.length && blogsResp?.meta?.total"
      class="ui-text-xs text-white text-center mt-4"
    >
      {{ blogs.length }} / {{ blogsResp.meta.total }}
      {{ $t("articles") }}
    </div>
    <div
      v-if="!isLoading && blogs.length > 0 && totalPages > 1"
      class="container flex justify-center mt-8"
    >
      <UiPagination
        v-model="filter.page"
        :total-pages="totalPages"
        @update:model-value="changePage"
        white
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash-es";
import { hash } from "ohash";

const route = useRoute();
const router = useRouter();
const { $api } = useNuxtApp();
const { setBlogListPageLdSchema } = useJsonLdSchema();

interface FilterI {
  page: number;
  count: number;
  search: string;
  categories?: string[];
}

const filter = computed(() => {
  const params: FilterI = {
    page: Number(route.query.page) || 1,
    count: 8,
    search: (route.query.search as string) || "",
  };

  if (route?.params?.section) {
    params.categories = [route?.params?.section as string];
  }

  return params;
});

const debouncedFilter = refDebounced(filter, 300);

const key = computed(() =>
  hash([(route.params?.section as string) || "all", unref(debouncedFilter)]),
);
const {
  data: blogsResp,
  pending: isLoading,
  error,
} = await useAsyncCachedData(
  key,
  () => {
    return $api.blog.getPosts(objectToQuery(debouncedFilter.value));
  },
  {
    watch: [debouncedFilter],
  },
);

if (error.value) {
  console.error("Error message:", error.value);
}

const blogs = computed(() => blogsResp.value?.data || []);
const totalPages = computed(
  () => (blogsResp.value?.meta?.last_page as number) || 1,
);

const scrollToCategoriesAndSearch = debounce(() => {
  const element = document.querySelector("#list");
  if (element) {
    const offset = -60;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  }
}, 150);

watch(
  () => filter.value.page,
  () => {
    scrollToCategoriesAndSearch();
  },
);

watch(
  () => route.query.search,
  () => {
    changePage(1);
    scrollToCategoriesAndSearch();
  },
);

const blogPageSchemaName = inject(blogPageSchemaNameKey);

setBlogListPageLdSchema({
  name: blogPageSchemaName,
  posts: blogs,
});

function changePage(page) {
  router.replace({ query: { ...route.query, page } });
}
</script>
